import type { Task, UpdateTaskFieldsInput, UpdateTaskInput } from "@recobra/schemas/task";

/** How a pending PATCH is reflected in the cache; mirrors `updateTask`'s merge rules. */
export const applyOptimisticUpdate = (task: Task, input: UpdateTaskInput): Task => {
  if ("transition" in input) {
    return input.transition === "reopen" ? { ...task, status: "todo", waitingStatus: null } : task;
  }
  return {
    ...task,
    description: input.description !== undefined ? input.description : task.description,
    priority: input.priority !== undefined ? input.priority : task.priority,
    deadlineDate: input.deadlineDate ?? task.deadlineDate,
    assignedTo: input.assignedTo !== undefined ? input.assignedTo : task.assignedTo,
    requestedDocuments: optimisticRequestedDocuments(
      task.requestedDocuments,
      input.requestedDocuments,
    ),
    dvcSearchDetails:
      input.dvcSearchDetails !== undefined ? input.dvcSearchDetails : task.dvcSearchDetails,
    status: input.status ?? task.status,
    waitingStatus:
      (input.status ?? task.status) === "waiting"
        ? (input.waitingStatus ?? task.waitingStatus ?? null)
        : null,
    solution: input.solution !== undefined ? input.solution : task.solution,
    notes: input.notes ?? task.notes,
    requirementProgress: {
      satisfied: mergeStamps(
        task.requirementProgress.satisfied,
        input.satisfyRequirements,
        input.waiveRequirements,
      ),
      waived: mergeStamps(
        task.requirementProgress.waived,
        input.waiveRequirements,
        input.satisfyRequirements,
      ),
    },
  };
};

const optimisticRequestedDocuments = (
  storedDocuments: Task["requestedDocuments"],
  requestedDocuments: UpdateTaskFieldsInput["requestedDocuments"],
) =>
  requestedDocuments?.map((document, index) => ({
    ...document,
    key: document.key ?? `optimistic_${index}`,
  })) ?? storedDocuments;

const mergeStamps = (
  stamps: Task["requirementProgress"]["satisfied"],
  patch: Record<string, boolean> | undefined,
  otherAnswerPatch: Record<string, boolean> | undefined,
): Task["requirementProgress"]["satisfied"] => {
  if (!patch && !otherAnswerPatch) return stamps;
  const merged = { ...stamps };

  for (const [key, on] of Object.entries(patch ?? {})) {
    if (on) merged[key] ??= { by: "", at: new Date().toISOString() };
    else delete merged[key];
  }
  for (const [key, on] of Object.entries(otherAnswerPatch ?? {})) if (on) delete merged[key];

  return merged;
};
